import {
  ReportId,
  ReportIds,
  ReportInputParameterKeys,
  ReportNtpDisplayModes,
  reportInputParametersById,
} from 'ag-common-lib/lib/contracts/reporting-api.contracts';
import { ProductionReportTemplateSettings, ProductionReportVariable } from './production-reports.models';

export const PRODUCTION_REPORTS_DEFAULT_TEMPLATE: ProductionReportTemplateSettings = {
  title: 'Consolidated Data Report',
  subtitle: 'Alliance Group',
  rowsPerPage: 20,
  logoUrl: '/assets/ag-logo-large.png',
};

/** Labels for fields available in the current form. Deprecated API bridge fields have no UI label. */
export const PRODUCTION_REPORT_VARIABLE_LABELS: Partial<Record<ReportInputParameterKeys, string>> = {
  [ReportInputParameterKeys.startDate]: 'Start Date',
  [ReportInputParameterKeys.endDate]: 'End Date',
  [ReportInputParameterKeys.displayThreshold]: 'Display Threshold',
  [ReportInputParameterKeys.includeThreshold]: 'Include Threshold',
  [ReportInputParameterKeys.ntpDisplayMode]: 'Show NTP',
  [ReportInputParameterKeys.showSuppressedAgents]: 'Show Suppressed Agents',
  [ReportInputParameterKeys.showTotals]: 'Show Totals',
};

type ProductionReportVariableValue = string | number | boolean;

/**
 * Suppression is offered only where the rows are agents (see reportInputParametersById):
 * an MGA or RMD row is not a person, so hiding one took no name off the page and only
 * understated the row. Reports without the parameter get no default for it.
 */
export const PRODUCTION_REPORT_BOOLEAN_DEFAULTS: Record<
  ReportId,
  Readonly<Partial<Record<ReportInputParameterKeys, boolean>>>
> = {
  [ReportIds.mgaPaid]: {
    [ReportInputParameterKeys.showTotals]: true,
  },
  [ReportIds.mgaSubmit]: {
    [ReportInputParameterKeys.showTotals]: true,
  },
  [ReportIds.personalPaid]: {
    [ReportInputParameterKeys.showSuppressedAgents]: false,
    [ReportInputParameterKeys.showTotals]: true,
  },
  [ReportIds.personalSubmit]: {
    [ReportInputParameterKeys.showSuppressedAgents]: false,
    [ReportInputParameterKeys.showTotals]: true,
  },
  [ReportIds.rmdPaid]: {
    [ReportInputParameterKeys.showTotals]: true,
  },
  [ReportIds.rmdSubmit]: {
    [ReportInputParameterKeys.showTotals]: true,
  },
};

export const getProductionReportBooleanDefaults = (reportId: ReportId) => PRODUCTION_REPORT_BOOLEAN_DEFAULTS[reportId];

/** Every report filters by its threshold unless the viewer switches it off. */
export const PRODUCTION_REPORT_INCLUDE_THRESHOLD_DEFAULT = true;

const REPORT_TYPE_DEFAULT_THRESHOLDS: Partial<Record<ReportId, number>> = {
  [ReportIds.mgaPaid]: 3000,
  [ReportIds.personalPaid]: 3000,
  [ReportIds.rmdPaid]: 3000,
  [ReportIds.mgaSubmit]: 1,
  [ReportIds.personalSubmit]: 1,
  [ReportIds.rmdSubmit]: 1,
};

export const buildReportVariables = (
  reportId: ReportId,
  overrides: Partial<Record<ReportInputParameterKeys, ProductionReportVariableValue>> = {},
): ProductionReportVariable[] => {
  const defaults = {
    ...getProductionReportBooleanDefaults(reportId),
    [ReportInputParameterKeys.includeThreshold]: PRODUCTION_REPORT_INCLUDE_THRESHOLD_DEFAULT,
    ...(REPORT_TYPE_DEFAULT_THRESHOLDS[reportId] !== undefined
      ? {
          [ReportInputParameterKeys.displayThreshold]: REPORT_TYPE_DEFAULT_THRESHOLDS[reportId],
        }
      : {}),
    ...overrides,
  };

  const isPersonalReport = reportId === ReportIds.personalPaid || reportId === ReportIds.personalSubmit;

  return reportInputParametersById[reportId].map(parameter => ({
    name: parameter.key,
    label: parameter.key === ReportInputParameterKeys.ntpDisplayMode
      ? `Show NTP ${isPersonalReport ? 'Agents' : 'Agencies'}`
      : PRODUCTION_REPORT_VARIABLE_LABELS[parameter.key],
    type: parameter.type,
    required: parameter.required,
    defaultValue: parameter.key === ReportInputParameterKeys.ntpDisplayMode
      ? defaults[parameter.key] ?? ReportNtpDisplayModes.separate
      : defaults[parameter.key],
    ...(parameter.key === ReportInputParameterKeys.ntpDisplayMode
      ? {
          options: [
            { value: ReportNtpDisplayModes.hide, label: 'No' },
            { value: ReportNtpDisplayModes.separate, label: `Display NTP ${isPersonalReport ? 'Agents' : 'Agencies'} Separately` },
            { value: ReportNtpDisplayModes.generalList, label: `Display NTP ${isPersonalReport ? 'Agents' : 'Agencies'} in General List` },
          ],
        }
      : {}),
  }));
};
